import { useState } from "react";
import { Sparkles, Camera, ChefHat, Loader2, X, Plus, CheckCircle2, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from "../ui/sheet";

/**
 * Asistente IA — Floating Action Button + Sheet drawer.
 *
 * UX rationale (Feb 2026 refactor):
 *  - Replaces the two large cards "Genera imágenes" + "Genera recetas" that
 *    used to take ~40% of the viewport above the dish grid.
 *  - Hidden behind a FAB so the user only sees the grid by default; opens
 *    on demand from any tab.
 *  - All tap targets ≥ 48 px (Apple HIG / Material 3 guidelines).
 *  - The Sheet stacks both actions vertically; each row is a self-contained
 *    panel with: title + pending badge + main CTA + selector + extras.
 *
 * Props:
 *  - allDishes: full list of dishes (for selector).
 *  - dishesWithoutImages: number — for the FAB badge.
 *  - dishesWithoutRecipes: number — for the FAB badge.
 *  - dishesWithRecipesSet, dishesWithProRecipeSet: Set<dish_id> — selector hints.
 *  - selectedForImages, setSelectedForImages: Set + setter.
 *  - selectedForRecipes, setSelectedForRecipes: Set + setter.
 *  - imgGenActive, recipeGenActive: booleans — disable buttons + spinner.
 *  - onGenerateImages(selectedSet): callback when user confirms image-gen.
 *  - onGenerateRecipes(selectedSet, { mode }): callback when user confirms recipe-gen.
 *  - restaurantBgPhotos, onUploadBgPhoto, onRemoveBgPhoto, uploadingBg, bgPhotoInputRef:
 *    background photo gallery state (passed straight from parent).
 *  - recipeBulkMode, setRecipeBulkMode: "simple" | "professional".
 */
export default function AsistenteIAFab({
  allDishes,
  dishesWithoutImages = 0,
  dishesWithoutRecipes = 0,
  dishesWithRecipesSet,
  dishesWithProRecipeSet,
  selectedForImages, setSelectedForImages,
  selectedForRecipes, setSelectedForRecipes,
  imgGenActive, recipeGenActive,
  onGenerateImages, onGenerateRecipes,
  restaurantBgPhotos = [], onUploadBgPhoto, onRemoveBgPhoto,
  uploadingBg, bgPhotoInputRef,
  recipeBulkMode = "simple", setRecipeBulkMode,
}) {
  const [open, setOpen] = useState(false);
  const [imgSelectOpen, setImgSelectOpen] = useState(false);
  const [recipeSelectOpen, setRecipeSelectOpen] = useState(false);

  const totalPending = dishesWithoutImages + dishesWithoutRecipes;

  // ── FAB badge label (only shown when there's something pending) ──
  const fabBadge = totalPending > 0
    ? (dishesWithoutImages > 0 && dishesWithoutRecipes > 0
        ? `${dishesWithoutImages}+${dishesWithoutRecipes}`
        : String(totalPending))
    : null;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      {/* ─── Floating Action Button (sticky bottom-right) ────────── */}
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label="Abrir Asistente IA"
          data-testid="ia-assistant-fab"
          className="fixed bottom-24 right-6 z-50 h-14 min-w-[56px] px-5 rounded-full
                     bg-primary text-primary-foreground shadow-lg hover:shadow-xl
                     hover:scale-[1.03] active:scale-[0.97] transition-all
                     flex items-center gap-2.5 font-medium text-sm
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" /* z-stack-doc: L5 AI assistant FAB; the Sheet it opens is also L5 (Radix); the CreditCheckDialog spawned from inside is at L6 */
        >
          <Sparkles className="w-5 h-5 shrink-0" />
          <span className="hidden sm:inline">Asistente IA</span>
          {fabBadge && (
            <span
              data-testid="ia-assistant-fab-badge"
              className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5
                         rounded-full bg-amber-400 text-amber-950 text-[11px] font-bold leading-none
                         tabular-nums shrink-0"
              title={`${dishesWithoutImages} sin imagen · ${dishesWithoutRecipes} sin receta`}
            >
              {fabBadge}
            </span>
          )}
        </button>
      </SheetTrigger>

      {/* ─── Sheet (slide-in drawer) ───────────────────────────── */}
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto p-0"
        data-testid="ia-assistant-sheet"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b sticky top-0 bg-card z-10">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 text-left">
              <SheetTitle className="text-lg">Asistente IA</SheetTitle>
              <SheetDescription className="text-xs">
                Genera imágenes y recetas para tus platos en lote.
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="p-5 space-y-5">
          {/* ─── Section: Image generation ──────────────────────── */}
          <section data-testid="ia-section-images">
            <header className="flex items-center justify-between gap-2 mb-2.5">
              <div className="flex items-center gap-2 min-w-0">
                <Camera className="w-4 h-4 text-primary shrink-0" />
                <h3 className="font-heading font-semibold text-base">Imágenes de platos</h3>
              </div>
              {dishesWithoutImages > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedForImages(new Set(allDishes.filter(d => !d.image_url).map(d => d.dish_id)));
                  }}
                  className="text-[11px] font-medium px-2 py-1 rounded-full
                             bg-amber-100 text-amber-700 border border-amber-200
                             hover:bg-amber-200 transition shrink-0"
                  data-testid="ia-img-pending-pill"
                >
                  {dishesWithoutImages} sin imagen
                </button>
              )}
            </header>

            <p className="text-xs text-muted-foreground mb-3">
              IA crea una foto profesional única para cada plato.
            </p>

            <Button
              onClick={() => {
                // Close the Sheet first — otherwise its Radix overlay
                // (also z-50) intercepts pointer events on the
                // CreditCheckDialog that opens immediately after, making
                // its buttons appear "dead to clicks" (Feb 2026 iter.210).
                setOpen(false);
                onGenerateImages(selectedForImages);
              }}
              disabled={imgGenActive}
              size="lg"
              className="w-full h-12 gap-2 text-sm font-medium"
              data-testid="ia-generate-images-btn"
            >
              {imgGenActive
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Generando…</>
                : <>
                    <Camera className="w-4 h-4" />
                    {selectedForImages.size > 0
                      ? `Generar ${selectedForImages.size} imágenes seleccionadas`
                      : "Generar imágenes"}
                  </>}
            </Button>

            {/* Selector toggle */}
            <button
              type="button"
              onClick={() => setImgSelectOpen(o => !o)}
              className="w-full mt-2 text-xs text-muted-foreground hover:text-foreground
                         flex items-center justify-center gap-1 py-2"
              data-testid="ia-img-toggle-selector"
            >
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${imgSelectOpen ? "rotate-180" : ""}`} />
              {imgSelectOpen ? "Ocultar selector" : "Elegir platos manualmente"}
            </button>

            {imgSelectOpen && (
              <SelectorPanel
                dishes={allDishes}
                selected={selectedForImages}
                setSelected={setSelectedForImages}
                doneSet={new Set(allDishes.filter(d => d.image_url).map(d => d.dish_id))}
              />
            )}

            {/* Background photos */}
            <div className="mt-4 pt-3 border-t border-border/40">
              <p className="text-[11px] text-muted-foreground mb-2 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" />
                {restaurantBgPhotos.length > 0
                  ? `${restaurantBgPhotos.length} foto${restaurantBgPhotos.length > 1 ? "s" : ""} de fondo · IA las combinará`
                  : "Opcional: fondos de tu restaurante"}
              </p>
              <div className="flex flex-wrap gap-2">
                {restaurantBgPhotos.map((photo, idx) => (
                  <div key={`bg-${photo.slice(-12)}`} className="relative group shrink-0">
                    <img src={photo} alt={`Fondo ${idx + 1}`} className="w-16 h-12 object-cover rounded-lg border border-border/60" />
                    <button
                      type="button"
                      onClick={() => onRemoveBgPhoto(idx)}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center text-xs leading-none"
                      data-testid={`ia-remove-bg-${idx}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => bgPhotoInputRef?.current?.click()}
                  disabled={uploadingBg}
                  className="h-12 w-16 border-2 border-dashed border-border/60 rounded-lg
                             text-muted-foreground hover:text-foreground hover:border-primary/40
                             flex items-center justify-center shrink-0"
                  data-testid="ia-upload-bg-btn"
                >
                  {uploadingBg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-5 h-5" />}
                </button>
              </div>
              <input
                type="file" ref={bgPhotoInputRef} onChange={onUploadBgPhoto}
                accept="image/jpeg,image/png,image/webp" multiple className="hidden"
              />
            </div>
          </section>

          <div className="border-t border-border" />

          {/* ─── Section: Recipe generation ────────────────────── */}
          <section data-testid="ia-section-recipes">
            <header className="flex items-center justify-between gap-2 mb-2.5">
              <div className="flex items-center gap-2 min-w-0">
                <ChefHat className="w-4 h-4 text-emerald-700 shrink-0" />
                <h3 className="font-heading font-semibold text-base">Recetas de platos</h3>
              </div>
              {dishesWithoutRecipes > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedForRecipes(new Set(allDishes.filter(d => !dishesWithRecipesSet?.has(d.dish_id)).map(d => d.dish_id)));
                  }}
                  className="text-[11px] font-medium px-2 py-1 rounded-full
                             bg-emerald-100 text-emerald-700 border border-emerald-200
                             hover:bg-emerald-200 transition shrink-0"
                  data-testid="ia-recipe-pending-pill"
                >
                  {dishesWithoutRecipes} sin receta
                </button>
              )}
            </header>

            <p className="text-xs text-muted-foreground mb-3">
              IA genera ingredientes, pasos, tiempos y consejos del chef.
            </p>

            {/* Mode toggle — Simple / Profesional */}
            <div
              className="grid grid-cols-2 gap-2 p-1 rounded-lg bg-muted/50 mb-3"
              role="tablist"
              aria-label="Modo de generación"
              data-testid="ia-recipe-mode-toggle"
            >
              <button
                type="button"
                role="tab"
                aria-selected={recipeBulkMode === "simple"}
                onClick={() => setRecipeBulkMode("simple")}
                className={`px-3 py-2.5 rounded-md text-xs font-medium transition ${
                  recipeBulkMode === "simple"
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="ia-mode-simple"
              >
                Simple
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={recipeBulkMode === "professional"}
                onClick={() => setRecipeBulkMode("professional")}
                className={`px-3 py-2.5 rounded-md text-xs font-medium transition ${
                  recipeBulkMode === "professional"
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="ia-mode-professional"
              >
                Profesional <span className="opacity-60 text-[10px]">+ bases</span>
              </button>
            </div>

            {recipeBulkMode === "professional" && (() => {
              const legacyCount = allDishes.filter(
                d => dishesWithRecipesSet?.has(d.dish_id) && !dishesWithProRecipeSet?.has(d.dish_id),
              ).length;
              if (legacyCount === 0) return null;
              return (
                <div
                  className="mb-3 px-2.5 py-2 rounded-md bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-900/40 text-[11px] text-violet-700 dark:text-violet-300 flex items-center gap-1.5"
                  data-testid="ia-legacy-hint"
                >
                  <Sparkles className="w-3.5 h-3.5 shrink-0" />
                  <span>
                    {legacyCount} receta{legacyCount === 1 ? "" : "s"} legacy se refinará{legacyCount === 1 ? "" : "n"} a versión profesional.
                  </span>
                </div>
              );
            })()}

            <Button
              onClick={() => {
                setOpen(false);
                onGenerateRecipes(selectedForRecipes, { mode: recipeBulkMode });
              }}
              disabled={recipeGenActive}
              size="lg"
              className="w-full h-12 gap-2 text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="ia-generate-recipes-btn"
            >
              {recipeGenActive
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Generando…</>
                : <>
                    <ChefHat className="w-4 h-4" />
                    {selectedForRecipes.size > 0
                      ? `Generar ${selectedForRecipes.size} recetas seleccionadas`
                      : (recipeBulkMode === "professional" ? "Generar recetas profesionales" : "Generar recetas")}
                  </>}
            </Button>

            {/* Selector toggle */}
            <button
              type="button"
              onClick={() => setRecipeSelectOpen(o => !o)}
              className="w-full mt-2 text-xs text-muted-foreground hover:text-foreground
                         flex items-center justify-center gap-1 py-2"
              data-testid="ia-recipe-toggle-selector"
            >
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${recipeSelectOpen ? "rotate-180" : ""}`} />
              {recipeSelectOpen ? "Ocultar selector" : "Elegir platos manualmente"}
            </button>

            {recipeSelectOpen && (
              <SelectorPanel
                dishes={allDishes}
                selected={selectedForRecipes}
                setSelected={setSelectedForRecipes}
                doneSet={dishesWithRecipesSet}
              />
            )}

            {(imgGenActive || recipeGenActive) && (
              <div className="mt-3 pt-3 border-t border-border/40">
                <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Generando en segundo plano. Te avisaremos al terminar.
                </p>
              </div>
            )}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Manual dish selector — shared between image & recipe sections.
 * "doneSet" highlights dishes that already have the asset (image or recipe)
 * with a green check.
 */
function SelectorPanel({ dishes, selected, setSelected, doneSet }) {
  return (
    <div className="mt-2 border border-border/60 rounded-lg p-3 space-y-2 bg-background/70" data-testid="ia-selector-panel">
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span className="font-medium">{selected.size > 0 ? `${selected.size} seleccionados` : "Selecciona platos"}</span>
        <div className="flex gap-3">
          <button onClick={() => setSelected(new Set(dishes.map(d => d.dish_id)))} className="hover:text-foreground underline">Todos</button>
          <button onClick={() => setSelected(new Set())} className="hover:text-foreground underline">Ninguno</button>
        </div>
      </div>
      <div className="max-h-48 overflow-y-auto space-y-0.5">
        {dishes.map(dish => (
          <label key={dish.dish_id} className="flex items-center gap-2 text-sm cursor-pointer p-1.5 rounded hover:bg-muted/50">
            <Checkbox
              checked={selected.has(dish.dish_id)}
              onCheckedChange={(checked) => {
                const next = new Set(selected);
                if (checked) next.add(dish.dish_id); else next.delete(dish.dish_id);
                setSelected(next);
              }}
            />
            <span className="flex-1 truncate text-xs">{dish.name}</span>
            {doneSet?.has(dish.dish_id) && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
          </label>
        ))}
      </div>
    </div>
  );
}
